import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import AppLayout from '@/components/layout/AppLayout';
import ErrorBoundary from '@/components/common/ErrorBoundary';

// Lazy-loaded pages
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const Home = lazy(() => import('@/pages/Home'));
const Conversations = lazy(() => import('@/pages/Conversations'));
const Settings = lazy(() => import('@/pages/Settings'));
const TestChat = lazy(() => import('@/pages/TestChat'));
const BusinessOps = lazy(() => import('@/pages/BusinessOps'));
const Catalog = lazy(() => import('@/pages/Catalog'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Growth = lazy(() => import('@/pages/Growth'));
const NewOrder = lazy(() => import('@/pages/NewOrder'));
// OrdersHub folds the old Orders + Calendar + Recurring pages into one tabbed
// surface; KnowledgeHub folds Knowledge Base + Documents + Memory + Vault.
const OrdersHub = lazy(() => import('@/pages/OrdersHub'));
const KnowledgeHub = lazy(() => import('@/pages/KnowledgeHub'));
const Receipt = lazy(() => import('@/pages/Receipt'));
const Team = lazy(() => import('@/pages/Team'));
const Roles = lazy(() => import('@/pages/Roles'));
const RoleDetail = lazy(() => import('@/pages/RoleDetail'));
const AgentWorkspace = lazy(() => import('@/pages/AgentWorkspace'));
const Connections = lazy(() => import('@/pages/Connections'));
const SetNewPassword = lazy(() => import('@/pages/SetNewPassword'));
const Insights = lazy(() => import('@/pages/Insights'));
const Admin = lazy(() => import('@/pages/Admin'));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
        <p className="text-sm text-muted">Loading...</p>
      </div>
    </div>
  );
}

/** Wraps each page in both Suspense (for lazy loading) and ErrorBoundary (for runtime crashes) */
function SafePage({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

/** Per-tenant feature gating — a disabled feature's page redirects home
 * instead of rendering (backend enforces separately via requireFeature). */
function FeatureGate({ feature, children }: { feature: string; children: ReactNode }) {
  const { hasFeature, isLoading } = useTenant();
  if (!isLoading && !hasFeature(feature)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Platform-admin-only routes (/admin). */
function PlatformAdminGate({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  if (!isLoading && user?.isPlatformAdmin !== true) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Operator-only pages — a simple-mode subscriber is redirected home so a
 * hidden nav item can't be reached by direct URL. */
function SubscriberGuard({ children }: { children: ReactNode }) {
  const { isSimpleMode, isLoading } = useTenant();
  if (!isLoading && isSimpleMode) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg">
        <div className="animate-spin w-10 h-10 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public routes — signup + recovery live outside ProtectedRoute */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Forced first-login password change for invited members — needs a
            session but renders full-screen like Login, so it lives outside
            AppLayout. */}
        <Route
          path="/set-password"
          element={
            <ProtectedRoute>
              <SafePage><SetNewPassword /></SafePage>
            </ProtectedRoute>
          }
        />

        {/* Protected routes inside app layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Home — simplified three-bucket landing for non-technical owners. */}
          <Route path="/" element={<SafePage><Home /></SafePage>} />
          {/* Mission Control (MyDay) retired from the nav — its fleet manifest
              + observability overlapped Home/Insights. Bookmark redirects home. */}
          <Route path="/mission-control" element={<Navigate to="/" replace />} />
          {/* Cut AL — /insights replaces standalone /metrics + /analytics
              with a tabbed view; legacy paths redirect into the right tab. */}
          <Route path="/insights" element={<SafePage><Insights /></SafePage>} />
          <Route path="/metrics" element={<Navigate to="/insights?tab=overview" replace />} />
          <Route path="/analytics" element={<Navigate to="/insights?tab=analytics" replace />} />
          {/* Cut AL — Bot Fleet merged into Agent Fleet; deep-link kept as
              redirect so any saved /bots URL still resolves. Bot Builder was
              folded into Agent Fleet too — same redirect treatment. */}
          <Route path="/bots" element={<Navigate to="/roles" replace />} />
          <Route path="/bot-builder" element={<Navigate to="/roles" replace />} />
          <Route path="/test-chat" element={<SafePage><TestChat /></SafePage>} />
          <Route path="/conversations" element={<SafePage><Conversations /></SafePage>} />
          {/* Policies dissolved into Settings ("Bot guardrails") — bookmark
              redirects there. */}
          <Route path="/policies" element={<Navigate to="/settings" replace />} />
          <Route path="/settings" element={<SafePage><Settings /></SafePage>} />
          {/* Knowledge hub — Q&A / Documents / Memory / Vault as tabs. Old
              standalone paths redirect into the matching tab. */}
          <Route path="/knowledge" element={<SafePage><KnowledgeHub /></SafePage>} />
          <Route path="/documents" element={<Navigate to="/knowledge?tab=docs" replace />} />
          <Route path="/memory" element={<Navigate to="/knowledge?tab=memory" replace />} />
          <Route path="/vault" element={<Navigate to="/knowledge?tab=vault" replace />} />
          <Route path="/catalog" element={<SafePage><Catalog /></SafePage>} />
          <Route path="/business-ops" element={<SafePage><BusinessOps /></SafePage>} />
          <Route path="/onboarding" element={<SafePage><Onboarding /></SafePage>} />
          <Route path="/growth" element={<SafePage><FeatureGate feature="growth"><Growth /></FeatureGate></SafePage>} />
          {/* Orders hub — List / Calendar / Recurring as tabs, each a real
              route. /calendar redirects into the Calendar tab. */}
          <Route path="/orders/new" element={<SafePage><NewOrder /></SafePage>} />
          <Route path="/orders/:bookingId/receipt" element={<SafePage><Receipt /></SafePage>} />
          <Route path="/orders" element={<SafePage><OrdersHub /></SafePage>} />
          <Route path="/orders/calendar" element={<SafePage><OrdersHub /></SafePage>} />
          <Route path="/orders/recurring" element={<SafePage><OrdersHub /></SafePage>} />
          <Route path="/calendar" element={<Navigate to="/orders/calendar" replace />} />
          <Route path="/team" element={<SafePage><Team /></SafePage>} />
          <Route path="/roles" element={<SubscriberGuard><SafePage><Roles /></SafePage></SubscriberGuard>} />
          <Route path="/roles/:name" element={<SubscriberGuard><SafePage><RoleDetail /></SafePage></SubscriberGuard>} />
          <Route path="/agents/:kind" element={<SafePage><AgentWorkspace /></SafePage>} />
          <Route path="/connections" element={<SafePage><Connections /></SafePage>} />
          {/* Welcome page retired — onboarding checklist covers first-run. */}
          <Route path="/welcome" element={<Navigate to="/onboarding" replace />} />
          <Route path="/admin" element={<SafePage><PlatformAdminGate><Admin /></PlatformAdminGate></SafePage>} />
        </Route>
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
